import React, { useState } from 'react';
import { 
  Clock, 
  CreditCard, 
  Package, 
  Truck, 
  CheckCircle, 
  XCircle,
  ChevronDown,
  Loader2
} from 'lucide-react';

export type OrderStatus = 
  | 'PENDING_PAYMENT' 
  | 'PAID' 
  | 'PROCESSING' 
  | 'SHIPPED' 
  | 'DELIVERED' 
  | 'CANCELLED';

interface OrderStatusSelectorProps {
  currentStatus: OrderStatus;
  onStatusChange: (newStatus: OrderStatus) => Promise<void>;
  disabled?: boolean;
}

const statusOptions: { value: OrderStatus; label: string; color: string; bg: string; icon: React.ReactNode }[] = [
  { 
    value: 'PENDING_PAYMENT', 
    label: 'Pendiente de pago', 
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    icon: <Clock className="w-4 h-4" />
  },
  { 
    value: 'PAID', 
    label: 'Pagado', 
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    icon: <CreditCard className="w-4 h-4" />
  },
  { 
    value: 'PROCESSING', 
    label: 'En preparación', 
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    icon: <Package className="w-4 h-4" />
  },
  { 
    value: 'SHIPPED', 
    label: 'En camino', 
    color: 'text-indigo-700',
    bg: 'bg-indigo-100',
    icon: <Truck className="w-4 h-4" />
  },
  { 
    value: 'DELIVERED', 
    label: 'Entregado', 
    color: 'text-green-700',
    bg: 'bg-green-100',
    icon: <CheckCircle className="w-4 h-4" />
  },
  { 
    value: 'CANCELLED', 
    label: 'Cancelado', 
    color: 'text-red-700',
    bg: 'bg-red-100',
    icon: <XCircle className="w-4 h-4" />
  },
];

export const OrderStatusSelector: React.FC<OrderStatusSelectorProps> = ({
  currentStatus,
  onStatusChange,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentOption = statusOptions.find(s => s.value === currentStatus);
  const isCancelled = currentStatus === 'CANCELLED';

  const handleSelect = async (status: OrderStatus) => {
    if (status === currentStatus) {
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    try {
      await onStatusChange(status);
      setIsOpen(false);
    } catch (error) {
      console.error('Error cambiando estado:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium mb-2 text-gray-700">
        Estado del pedido
      </label>
      
      <button
        onClick={() => !disabled && !isLoading && !isCancelled && setIsOpen(!isOpen)}
        disabled={disabled || isLoading || isCancelled}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all ${
          isOpen 
            ? 'border-emerald-500 ring-2 ring-emerald-500/20' 
            : 'border-gray-200 hover:border-gray-300'
        } ${disabled || isCancelled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} bg-white`}
      >
        <div className="flex items-center gap-3">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
          ) : (
            <div className={`p-1.5 rounded-lg ${currentOption?.bg} ${currentOption?.color}`}>
              {currentOption?.icon}
            </div>
          )}
          <span className="font-medium text-gray-900">
            {isLoading ? 'Actualizando...' : currentOption?.label}
          </span>
        </div>
        {!isCancelled && (
          <ChevronDown className={`w-5 h-5 transition-transform ${
            isOpen ? 'rotate-180' : ''
          } text-gray-500`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && !isCancelled && (
        <>
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-2 rounded-xl shadow-lg border overflow-hidden bg-white border-gray-200">
            {statusOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelect(option.value)}
                className={`w-full flex items-center gap-3 px-4 py-3 transition-colors ${
                  option.value === currentStatus
                    ? 'bg-emerald-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className={`p-1.5 rounded-lg ${option.bg} ${option.color}`}>
                  {option.icon}
                </div>
                <span className={`flex-1 text-left ${
                  option.value === currentStatus
                    ? 'font-medium '
                    : ''
                } text-gray-900`}>
                  {option.label}
                </span>
                {option.value === currentStatus && (
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default OrderStatusSelector;
